/*
 * Copyright The OpenTelemetry Authors
 * SPDX-License-Identifier: Apache-2.0
 */
import { getNumberFromEnv, getStringFromEnv } from '@opentelemetry/core';
import { OtlpSharedConfiguration } from './shared-configuration';
import { diag } from '@opentelemetry/api';

function parseAndValidateTimeoutFromEnv(
  timeoutEnvVar: string
): number | undefined {
  const envTimeout = getNumberFromEnv(timeoutEnvVar);
  if (envTimeout != null) {
    if (Number.isFinite(envTimeout) && envTimeout > 0) {
      return envTimeout;
    }
    diag.warn(
      `Configuration: ${timeoutEnvVar} is invalid, expected number greater than 0 (actual: ${envTimeout})`
    );
  }

  return undefined;
}

function getTimeoutFromEnv(signalIdentifier: string) {
  const specificTimeout = parseAndValidateTimeoutFromEnv(
    `OTEL_EXPORTER_OTLP_${signalIdentifier}_TIMEOUT`
  );
  const nonSpecificTimeout = parseAndValidateTimeoutFromEnv(
    'OTEL_EXPORTER_OTLP_TIMEOUT'
  );

  return specificTimeout ?? nonSpecificTimeout;
}

function parseAndValidateCompressionFromEnv(
  compressionEnvVar: string
): 'none' | 'gzip' | undefined {
  const compression = getStringFromEnv(compressionEnvVar)?.trim();

  if (compression == null || compression === 'none' || compression === 'gzip') {
    return compression;
  }

  diag.warn(
    `Configuration: ${compressionEnvVar} is invalid, expected 'none' or 'gzip' (actual: '${compression}')`
  );
  return undefined;
}

function getCompressionFromEnv(
  signalIdentifier: string
): 'none' | 'gzip' | undefined {
  const specificCompression = parseAndValidateCompressionFromEnv(
    `OTEL_EXPORTER_OTLP_${signalIdentifier}_COMPRESSION`
  );
  const nonSpecificCompression = parseAndValidateCompressionFromEnv(
    'OTEL_EXPORTER_OTLP_COMPRESSION'
  );

  return specificCompression ?? nonSpecificCompression;
}

export function getSharedConfigurationFromEnvironment(
  signalIdentifier: string
): Partial<OtlpSharedConfiguration> {
  return {
    timeoutMillis: getTimeoutFromEnv(signalIdentifier),
    compression: getCompressionFromEnv(signalIdentifier),
  };
}
